import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  Box, Button, Toolbar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, List, ListItem, ListItemText, Typography, Snackbar, Alert, Switch, FormControlLabel, IconButton, Tooltip, Paper, Checkbox, LinearProgress, Chip, Grid, Card, CardContent,
  ListItemIcon, ListItemSecondaryAction, Menu, MenuItem,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, Save as SaveIcon, AddCircleOutline as AddIcon, FolderOpen as LoadIcon,
  FileDownload as ExportIcon, FileUpload as ImportIcon, Keyboard as KeyboardIcon,
  Close as CloseIcon, Timeline as TimelineIcon, Refresh as RefreshIcon, Stream as StreamingIcon, Refresh as RecoveryIcon, MoreVert as MoreVertIcon, Help as HelpIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FlowDesignTab from '../components/AgenticFlow/FlowDesignTab';
import EdgesTab from '../components/AgenticFlow/EdgesTab';
import StateManagementTab from '../components/AgenticFlow/StateManagementTab';
import FlowSettingsTab from '../components/AgenticFlow/FlowSettingsTab';
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from '../components/AgenticFlow/KeyboardShortcuts';
import EdgeDialog from '../components/AgenticFlow/EdgeDialog';
import StateVariableDialog from '../components/AgenticFlow/StateVariableDialog';
import {
  createAgenticFlow, executeAgenticFlow, getAgenticFlows, getAgenticFlowById, deleteAgenticFlow,
  executeFlowStreaming,
  recoverFlowExecution,
  executeFlowDebug,
  getFlowStatus,
} from '../api/agenticFlow.api';
import {
  ErrorRecoveryModal,
  InlineErrorRecovery,
} from '../components/ErrorRecovery';
import { createTemplate } from '../api/automation.api.js';
import { getLangGraphBaseUrl } from '../config/brand.js';
import { toDisplayString } from '../utils/flowResultUtils';
import { useFlowContext, FlowContextProvider } from '../contexts/FlowContext.jsx';
import FlowBuilderTabs from '../components/AgenticFlow/FlowBuilderTabs';
import MCPFlowIntegration from '../components/MCP/MCPFlowIntegration';
import DebugPanel from '../components/AgenticFlow/DebugPanel';

function AgenticFlowBuilder(props) {
  const darkMode = props.darkMode ?? false;
  const navigate = useNavigate();

  // Use MCP context props if provided, otherwise fallback to local state
  const [localNodes, setLocalNodes] = useState([]);
  const [localEdges, setLocalEdges] = useState([]);

  const nodes = props.nodes !== undefined ? props.nodes : localNodes;
  const setNodes = props.setNodes !== undefined ? props.setNodes : setLocalNodes;
  const edges = props.edges !== undefined ? props.edges : localEdges;
  const setEdges = props.setEdges !== undefined ? props.setEdges : setLocalEdges;

  // Main state
  const [selectedTab, setSelectedTab] = useState(0);
  const [globalState, setGlobalState] = useState({});
  const [stateSchema, setStateSchema] = useState({});
  const [flowName, setFlowName] = useState('Untitled Flow');
  const [flowDescription, setFlowDescription] = useState('');
  const [currentFlowId, setCurrentFlowId] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [savedFlows, setSavedFlows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [loadingFlowId, setLoadingFlowId] = useState(null);

  // Flow settings state
  const [flowSettings, setFlowSettings] = useState({
    // Execution Settings
    enableParallelExecution: true,
    autoSaveState: true,
    enableExecutionLogging: false,
    enableVariableSubstitution: true,
    enableDebugMode: false,
    maxExecutionTime: 300,
    executionTimeout: 30000,
    langgraphContinuationTimeout: 20,
    backgroundTaskTimeout: 30,

    // Error Handling
    onError: 'stop',
    maxRetryAttempts: 3,
    retryDelay: 5,
    logAllErrors: true,

    // State Management
    persistStateBetweenRuns: true,
    enableStateValidation: false,
    autoSaveStateVariables: true,
    enableStateEncryption: false,
    stateCleanupInterval: 24,
    maxStateSize: 10,

    // Security & Access
    enableSandboxedExecution: true,
    requireAuthentication: false,
    apiRateLimit: 60,
    enableAuditLogging: false,
    validateInputData: true,
    sessionTimeout: 30,

    // Performance & Optimization
    enableCaching: true,
    enableCompression: false,
    enablePerformanceMonitoring: false,
    optimizeMemoryUsage: true,
    cacheTTL: 3600,
    maxConcurrentNodes: 5,

    // Integration & External Services
    enableApiCalls: true,
    enableWebhookNotifications: false,
    enableExternalDatabaseConnections: false,
    enableEmailNotifications: true,
    defaultApiTimeout: 30,
    webhookRetryAttempts: 3,
    errorWorkflowId: null,
  });

  // Auto-save functionality
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const autoSaveTimeoutRef = useRef(null);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const lastSaveDataRef = useRef(null);
  const [hasBeenManuallySaved, setHasBeenManuallySaved] = useState(false);

  // Dialog state
  const [edgeDialogOpen, setEdgeDialogOpen] = useState(false);
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [currentEdge, setCurrentEdge] = useState(null);
  const [currentStateVar, setCurrentStateVar] = useState(null);

  // Node properties dialog state (Step 1)
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  // Flow states (added to fix reference error)
  const [flowStates, setFlowStates] = useState([]);
  const [flowTools, setFlowTools] = useState([]);

  // Add state for the checkbox
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  // Add state for Save As functionality
  const [isSaveAs, setIsSaveAs] = useState(false);
  const [originalFlowName, setOriginalFlowName] = useState('');

  // Export/Import JSON state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportJson, setExportJson] = useState('');

  // MCP Integration state - simplified to just track if MCP tab is active
  const [mcpTabActive, setMcpTabActive] = useState(false);
  const [flowVersions, setFlowVersions] = useState([]);

  // More Options Menu state
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [openDebugPanelDialog, setOpenDebugPanelDialog] = useState(false);
  const [debugPanelExecutionIdInput, setDebugPanelExecutionIdInput] = useState('');

  const {
    exportContext, importContext, setVar, currentNodeId, deleteVar,
  } = useFlowContext();
  const [tenantId, setTenantId] = useState('686543469fb1b7dc79743d31');

  // Show save dialog when Save Flow is clicked
  const handleOpenSaveDialog = () => setSaveDialogOpen(true);

  /* useEffect(() => {
  const handleTenantChange = (e) => {
    // e.detail.tenantId is the new tenant id
    // You can trigger data reload or filtering here
  };
  window.addEventListener('tenantChanged', handleTenantChange);
  return () => window.removeEventListener('tenantChanged', handleTenantChange);
}, []); */

  // Save workflow (called from dialog)
  const handleSaveFlow = async () => {
    if (!flowName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a flow name', severity: 'error' });
      setSaveDialogOpen(true);
      return null;
    }

    // Check if this is a "Save As" operation (name changed from original)
    const isSaveAsOperation = currentFlowId && flowName !== originalFlowName;

    try {
      // Sync stateSchema/globalState with MCP context before saving
      if (exportContext) {
        const mcp = exportContext();
        if (mcp && mcp.vars) {
          const newStateSchema = { ...stateSchema };
          const newGlobalState = { ...globalState };
          Object.entries(mcp.vars).forEach(([scope, scopeVars]) => {
            if (scope === 'global' || scope === 'flow') {
              Object.entries(scopeVars).forEach(([key, meta]) => {
                newStateSchema[key] = {
                  type: meta.type || 'string',
                  defaultValue: meta.value,
                  description: meta.description || '',
                  persistent: meta.persistent || false,
                  nodes: meta.nodes || [],
                };
                newGlobalState[key] = meta.value;
              });
            } else if (scope === 'node') {
              Object.entries(scopeVars).forEach(([nodeId, nodeVars]) => {
                Object.entries(nodeVars).forEach(([key, meta]) => {
                  newStateSchema[key] = {
                    type: meta.type || 'string',
                    defaultValue: meta.value,
                    description: meta.description || '',
                    persistent: meta.persistent || false,
                    nodes: [nodeId],
                  };
                });
              });
            }
          });
          setStateSchema(newStateSchema);
          setGlobalState(newGlobalState);
        }
      }

      // DEBUG: Log current state before save
      console.log('🔍 DEBUG - Manual Save - Current edges:', edges);
      console.log('🔍 DEBUG - Manual Save - Edges count:', edges.length);
      console.log('🔍 DEBUG - Manual Save - AI Agent edges exist:', edges.some((e) => e.source && e.source.startsWith('ai-agent_')));
      console.log('🔍 DEBUG - Manual Save - Form incoming edges exist:', edges.some((e) => e.target && e.target.startsWith('form_')));

      // DEBUG: Log the specific AI Agent edge if it exists
      const manualAiAgentEdge = edges.find((e) => e.source && e.source.startsWith('ai-agent_'));
      if (manualAiAgentEdge) {
        console.log('🔍 DEBUG - AI Agent edge details:', manualAiAgentEdge);
      } else {
        console.log('🔍 DEBUG - No AI Agent edge found in current state');
      }

      // DEBUG: Log all edges with their sources and targets
      console.log('🔍 DEBUG - All edges summary:');
      edges.forEach((edge, index) => {
        console.log(`🔍 DEBUG - Edge ${index}: ${edge.source} → ${edge.target}`);
      });

      // Use the latest nodes state for saving
      const updatedStateSchema = { ...stateSchema };

      // Check nodes for state variables and update stateSchema
      nodes.forEach((node) => {
        if (node.data && Array.isArray(node.data.stateVars)) {
          node.data.stateVars.forEach((stateVar) => {
            if (updatedStateSchema[stateVar]) {
              // Make sure this node is in the nodes list for this state var
              const stateNodes = updatedStateSchema[stateVar].nodes || [];
              if (!stateNodes.includes(node.id)) {
                updatedStateSchema[stateVar] = {
                  ...updatedStateSchema[stateVar],
                  nodes: [...stateNodes, node.id],
                };
              }
            } else {
              // If state variable doesn't exist in schema, create it
              // But first check if it exists in MCP context to preserve type information
              let type = 'string';
              let defaultValue = '';
              let description = `State variable for ${node.data.label || node.id}`;
              let persistent = false;

              // Check MCP context for existing type information
              if (exportContext) {
                const mcp = exportContext();
                if (mcp && mcp.contexts) {
                  // Check global scope first
                  if (mcp.contexts.global && mcp.contexts.global[stateVar]) {
                    const globalVar = mcp.contexts.global[stateVar];
                    type = globalVar.type || 'string';
                    defaultValue = globalVar.value !== undefined ? globalVar.value : '';
                    description = globalVar.description || description;
                    persistent = globalVar.persistent || false;
                  }
                  // Check flow scope
                  else if (mcp.contexts.flow && mcp.contexts.flow[stateVar]) {
                    const flowVar = mcp.contexts.flow[stateVar];
                    type = flowVar.type || 'string';
                    defaultValue = flowVar.value !== undefined ? flowVar.value : '';
                    description = flowVar.description || description;
                    persistent = flowVar.persistent || false;
                  }
                  // Check node scope
                  else if (mcp.contexts.node && mcp.contexts.node[node.id] && mcp.contexts.node[node.id][stateVar]) {
                    const nodeVar = mcp.contexts.node[node.id][stateVar];
                    type = nodeVar.type || 'string';
                    defaultValue = nodeVar.value !== undefined ? flowVar.value : '';
                    description = nodeVar.description || description;
                    persistent = nodeVar.persistent || false;
                  }
                }
              }

              updatedStateSchema[stateVar] = {
                type,
                defaultValue,
                description,
                persistent,
                nodes: [node.id],
              };
            }
          });
        }
      });

      // Convert stateSchema to array format for saving
      const stateArray = Object.entries(updatedStateSchema).map(([key, schema]) => ({
        name: key,
        ...schema,
      }));

      // DEBUG: Log edges before save
      console.log('🔍 DEBUG - Auto-save - Saving flow with edges:', edges);
      console.log('🔍 DEBUG - Auto-save - Edges count:', edges.length);
      console.log('🔍 DEBUG - Auto-save - AI Agent edges exist:', edges.some((e) => e.source && e.source.startsWith('ai-agent_')));
      console.log('🔍 DEBUG - Auto-save - Form incoming edges exist:', edges.some((e) => e.target && e.target.startsWith('form_')));

      // DEBUG: Log the specific AI Agent edge if it exists
      const autoAiAgentEdge = edges.find((e) => e.source && e.source.startsWith('ai-agent_'));
      if (autoAiAgentEdge) {
        console.log('🔍 DEBUG - Auto-save - AI Agent edge details:', autoAiAgentEdge);
      } else {
        console.log('🔍 DEBUG - Auto-save - No AI Agent edge found in current state');
      }

      // Debug logging to verify type information is preserved
      const flowData = {
        name: flowName,
        description: flowDescription,
        nodes, // Always use the latest nodes state
        edges,
        breakpoints: breakpoints.length > 0 ? breakpoints : undefined,
        globalState: {
          states: stateArray, // Use the converted stateSchema
          tools: flowTools,
        },
        settings: {
          version: '1.0.0',
          created: (currentFlowId && !isSaveAsOperation) ? undefined : new Date().toISOString(),
          updated: new Date().toISOString(),
          ...flowSettings, // Include all flow settings
        },
        mcpContext: exportContext ? exportContext() : null, // <-- persist MCP context
      };

      // For Save As operation, don't include the currentFlowId to create a new flow
      const saveData = isSaveAsOperation ? flowData : (currentFlowId ? { ...flowData, _id: currentFlowId } : flowData);

      const { response, err } = await createAgenticFlow(saveData);
      let newFlowId = null;
      if (response) {
        newFlowId = response.flowId || response.flow?._id;

        if (isSaveAsOperation) {
          // For Save As, update the current flow ID and original name
          setCurrentFlowId(newFlowId);
          setOriginalFlowName(flowName);
          setSnackbar({ open: true, message: `Flow saved as "${flowName}" successfully!`, severity: 'success' });
        } else {
          // For regular save, just update the flow ID
          setCurrentFlowId(newFlowId);
          setSnackbar({ open: true, message: 'Flow saved successfully!', severity: 'success' });
        }

        // Save the flow ID to localStorage for auto-loading
        localStorage.setItem('agenticFlowBuilder_lastFlowId', newFlowId);

        // Mark that the flow has been manually saved at least once
        setHasBeenManuallySaved(true);

        setSaveDialogOpen(false);
        loadSavedFlows(); // Refresh the list after save
        // If saveAsTemplate is checked, also save as template
        if (saveAsTemplate) {
          await handleSaveAsTemplate(true); // pass silent=true
        }
        return newFlowId;
      }
      setSnackbar({ open: true, message: err?.message || 'Failed to save flow', severity: 'error' });
      return null;
    } catch (error) {
      console.error('Error saving flow:', error);
      setSnackbar({ open: true, message: 'Failed to save flow', severity: 'error' });
      return null;
    }
  };

  // Auto-save function with debouncing
  const autoSaveFlow = useCallback(async (force = false) => {
    if (!autoSaveEnabled || !flowName.trim() || !hasBeenManuallySaved) {
      return;
    }

    // Create a hash of the current flow data to detect changes
    const currentFlowData = JSON.stringify({
      nodes,
      edges,
      flowName,
      flowDescription,
      globalState,
      stateSchema,
      flowSettings,
      breakpoints,
    });

    // Skip auto-save if no changes detected (unless forced)
    if (!force && lastSaveDataRef.current === currentFlowData) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');

        // Use the same save logic as handleSaveFlow but without user prompts
        const updatedStateSchema = { ...stateSchema };
        nodes.forEach((node) => {
          if (node.data && Array.isArray(node.data.stateVars)) {
            node.data.stateVars.forEach((stateVar) => {
              if (updatedStateSchema[stateVar]) {
                const stateNodes = updatedStateSchema[stateVar].nodes || [];
                if (!stateNodes.includes(node.id)) {
                  updatedStateSchema[stateVar] = {
                    ...updatedStateSchema[stateVar],
                    nodes: [...stateNodes, node.id],
                  };
                }
              } else {
                updatedStateSchema[stateVar] = {
                  type: 'string',
                  defaultValue: '',
                  description: `State variable for ${node.data.label || node.id}`,
                  persistent: false,
                  nodes: [node.id],
                };
              }
            });
          }
        });

        const stateArray = Object.entries(updatedStateSchema).map(([key, schema]) => ({
          name: key,
          ...schema,
        }));

        const flowData = {
          name: flowName,
          description: flowDescription,
          nodes,
          edges,
          breakpoints: breakpoints.length > 0 ? breakpoints : undefined,
          globalState: {
            states: stateArray,
            tools: flowTools,
          },
          settings: {
            version: '1.0.0',
            created: currentFlowId ? undefined : new Date().toISOString(),
            updated: new Date().toISOString(),
            ...flowSettings,
          },
          mcpContext: exportContext ? exportContext() : null,
        };

        const { response, err } = await createAgenticFlow(
          currentFlowId ? { ...flowData, _id: currentFlowId } : flowData,
        );

        if (response) {
          const newFlowId = response.flowId || response.flow?._id;
          if (!currentFlowId) {
            setCurrentFlowId(newFlowId);
          }
          setLastAutoSave(new Date());
          setAutoSaveStatus('saved');
          lastSaveDataRef.current = currentFlowData;

          // Show success message briefly
          setTimeout(() => setAutoSaveStatus('idle'), 2000);
        } else {
          setAutoSaveStatus('error');
          console.error('Auto-save failed:', err);
          setSnackbar({ open: true, message: 'Auto-save failed', severity: 'error' });
        }
      } catch (error) {
        setAutoSaveStatus('error');
        console.error('Auto-save error:', error);
        setSnackbar({ open: true, message: 'Auto-save error', severity: 'error' });
      }
    }, 2000); // 2 second debounce
  }, [autoSaveEnabled, flowName, nodes, edges, globalState, stateSchema, flowSettings, currentFlowId, exportContext, flowTools, hasBeenManuallySaved]);

  // Effect to trigger auto-save when flow changes
  useEffect(() => {
    if (autoSaveEnabled && flowName.trim()) {
      autoSaveFlow();
    }
  }, [nodes, edges, flowName, flowDescription, globalState, stateSchema, flowSettings, autoSaveFlow]);

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+S or Cmd+S for save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleOpenSaveDialog();
        autoSaveFlow(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [autoSaveFlow]);

  // Add a handler for saving as workflow template
  const handleSaveAsTemplate = async (silent = false) => {
    try {
      const updatedStateSchema = { ...stateSchema };
      nodes.forEach((node) => {
        if (node.data && Array.isArray(node.data.stateVars)) {
          node.data.stateVars.forEach((stateVar) => {
            if (updatedStateSchema[stateVar]) {
              const stateNodes = updatedStateSchema[stateVar].nodes || [];
              if (!stateNodes.includes(node.id)) {
                updatedStateSchema[stateVar] = {
                  ...updatedStateSchema[stateVar],
                  nodes: [...stateNodes, node.id],
                };
              }
            }
          });
        }
      });
      const stateArray = Object.entries(updatedStateSchema).map(([key, schema]) => ({
        name: key,
        ...schema,
      }));
      const templateData = {
        name: flowName,
        description: flowDescription,
        flowDefinition: {
          name: flowName,
          description: flowDescription,
          nodes,
          edges,
          globalState: {
            states: stateArray,
            tools: flowTools,
          },
          settings: {
            version: '1.0.0',
            created: new Date().toISOString(),
            updated: new Date().toISOString(),
          },
          mcpContext: exportContext(),
        },
      };
      await createTemplate(templateData);
      if (!silent) setSnackbar({ open: true, message: 'Saved as workflow template!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to save as template', severity: 'error' });
    }
  };

  // Modal state for execute flow
  const [executeModalOpen, setExecuteModalOpen] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugExecutionId, setDebugExecutionId] = useState(null);
  const [breakpoints, setBreakpoints] = useState([]);
  const [executePrompt, setExecutePrompt] = useState('');
  const [executeResponse, setExecuteResponse] = useState('');
  const [executeLoading, setExecuteLoading] = useState(false);
  const [executorBackend, setExecutorBackend] = useState('node'); // Default to 'node' backend

  // Streaming execution state
  const [streamingMode, setStreamingMode] = useState(true);
  const [streamingConnection, setStreamingConnection] = useState(null);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [currentExecutionNode, setCurrentExecutionNode] = useState(null);
  const [executionLog, setExecutionLog] = useState([]);
  const [nodeStatuses, setNodeStatuses] = useState({});
  const [realTimeUpdates, setRealTimeUpdates] = useState([]);

  // Error recovery state
  const [errorRecoveryState, setErrorRecoveryState] = useState({
    isVisible: false,
    error: null,
    recoveryOptions: null,
    selectedExecution: null,
  });
  const [isRecovering, setIsRecovering] = useState(false);
  const [canRecover, setCanRecover] = useState(false);

  // Monitoring dashboard state
  const [monitoringMode, setMonitoringMode] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [monitoringDialogOpen, setMonitoringDialogOpen] = useState(false);

  // Execute workflow with prompt modal
  const handleExecuteFlow = (asDebug = false) => {
    setExecutePrompt('');
    setExecuteResponse('');
    setDebugMode(asDebug);
    setExecuteModalOpen(true);
  };

  // Streaming execution function
  const startStreamingExecution = useCallback(async (flowDef, input) => {
    if (!streamingMode) {
      return null;
    }

    try {
      console.log('🚀 AgenticFlowBuilder: Starting streaming execution');
      setExecutionProgress(0);
      setCurrentExecutionNode(null);
      setExecutionLog([]);
      setNodeStatuses({});
      setRealTimeUpdates([]);
      setCanRecover(false);

      const streamConnection = await executeFlowStreaming(
        flowDef,
        input,
        // onUpdate callback - real-time updates
        (update) => {
          console.log('📡 AgenticFlowBuilder streaming update:', update.type, update.node_id);

          // Update progress
          if (update.progress?.percentage !== undefined) {
            setExecutionProgress(update.progress.percentage);
          }

          // Update current node
          if (update.node_id) {
            setCurrentExecutionNode(update.node_id);
          }

          // Update node statuses
          if (update.node_id && update.type) {
            setNodeStatuses((prev) => ({
              ...prev,
              [update.node_id]: update.type,
            }));
          }

          // Add to execution log (ensure message is string to avoid [object Object])
          const msg = update.message;
          const msgStr = typeof msg === 'string' ? msg : (msg != null ? JSON.stringify(msg) : `${update.type} - ${update.node_id || 'unknown'}`);
          setExecutionLog((prev) => [...prev, {
            timestamp: new Date().toISOString(),
            nodeId: update.node_id,
            type: update.type,
            message: msgStr,
            data: update,
          }]);

          // Add to real-time updates
          setRealTimeUpdates((prev) => [...prev, update]);

          // Handle flow completion
          if (update.type === 'flow_complete') {
            console.log('✅ AgenticFlowBuilder: Flow completed via streaming');
            setExecutionProgress(100);
            const completeContent = update.result?.data ?? update.result ?? update.data;
            setExecuteResponse(toDisplayString(completeContent, 'Flow completed successfully'));
          }

          // Handle form pause - show Debug Panel so user can inspect state and submit form
          if (update.type === 'form_pause' || Boolean(update.waiting_for_form_input)) {
            const execId = update.execution_id;
            if (execId) {
              setDebugExecutionId(execId);
              setExecuteModalOpen(false);
              const content = update.ai_response ?? update.data?.ai_response ?? update.result?.ai_response ?? update.result?.message;
              setExecuteResponse(toDisplayString(content, 'Flow paused at form. Use the Debug Panel to inspect or submit.'));
              setSnackbar({ open: true, message: 'Flow paused at form. Use the Debug Panel to inspect or continue.', severity: 'info' });
            }
          }

          // Handle flow errors
          if (update.type === 'flow_error') {
            console.error('❌ AgenticFlowBuilder: Flow error via streaming', update.error);
            setErrorRecoveryState({
              isVisible: true,
              error: update.error,
              recoveryOptions: update.recoveryOptions,
              selectedExecution: update.execution_id,
            });
            setCanRecover(true);
          }
        },
        // onError callback
        (error) => {
          console.error('❌ AgenticFlowBuilder: Streaming error', error);
          setSnackbar({
            open: true,
            message: `Streaming execution failed: ${error.message}`,
            severity: 'error',
          });
        },
      );

      setStreamingConnection(streamConnection);
      return streamConnection;
    } catch (error) {
      console.error('❌ AgenticFlowBuilder: Failed to start streaming', error);
      setSnackbar({
        open: true,
        message: `Failed to start streaming execution: ${error.message}`,
        severity: 'error',
      });
      return null;
    }
  }, [streamingMode]);

  // Error recovery handler
  const handleErrorRecovery = useCallback(async (executionId) => {
    if (!executionId) return;

    setIsRecovering(true);
    try {
      console.log('🔄 AgenticFlowBuilder: Starting error recovery for', executionId);

      const recoveryResult = await recoverFlowExecution(executionId);

      if (recoveryResult.success) {
        console.log('✅ AgenticFlowBuilder: Recovery successful');
        setCanRecover(false);
        setErrorRecoveryState({
          isVisible: false,
          error: null,
          recoveryOptions: null,
          selectedExecution: null,
        });
        setSnackbar({
          open: true,
          message: 'Flow recovered successfully!',
          severity: 'success',
        });
      } else {
        console.error('❌ AgenticFlowBuilder: Recovery failed:', recoveryResult.error);
        setSnackbar({
          open: true,
          message: `Recovery failed: ${recoveryResult.error}`,
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('AgenticFlowBuilder recovery error:', error);
      setSnackbar({
        open: true,
        message: `Recovery error: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsRecovering(false);
    }
  }, []);

  // Monitoring functions
  const fetchPerformanceMetrics = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBaseUrl();
      const response = await fetch(`${langgraphUrl}/debug/performance`);
      const data = await response.json();
      setPerformanceMetrics(data.performance_metrics || []);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    }
  }, []);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBaseUrl();
      const response = await fetch(`${langgraphUrl}/debug/status`);
      const data = await response.json();
      setSystemStatus(data);
    } catch (error) {
      console.error('Error fetching system status:', error);
    }
  }, []);

  const fetchDebugData = useCallback(async () => {
    try {
      const langgraphUrl = getLangGraphBaseUrl();
      const [performanceResponse, statusResponse, errorsResponse] = await Promise.all([
        fetch(`${langgraphUrl}/debug/performance`),
        fetch(`${langgraphUrl}/debug/status`),
        fetch(`${langgraphUrl}/debug/errors`),
      ]);

      const performanceData = await performanceResponse.json();
      const statusData = await statusResponse.json();
      const errorsData = await errorsResponse.json();

      setDebugData({
        performance: performanceData.performance_metrics || [],
        status: statusData,
        errors: errorsData.error_log || [],
      });
    } catch (error) {
      console.error('Error fetching debug data:', error);
    }
  }, []);

  const handleOpenMonitoring = useCallback(async () => {
    setMonitoringDialogOpen(true);
    if (monitoringMode) {
      await fetchDebugData();
    }
  }, [monitoringMode, fetchDebugData]);

  // Actually run the flow when user submits prompt
  const handleRunFlowWithPrompt = async () => {
    setExecuteLoading(true);
    try {
      const id = await handleSaveFlow();
      if (!id) {
        setSnackbar({ open: true, message: 'Save the flow before executing.', severity: 'warning' });
        setExecuteLoading(false);
        return;
      }
      // Build the flow definition from state
      let stateArray = Object.entries(stateSchema).map(([key, schema]) => ({
        name: key,
        type: schema.type || 'string', // Ensure type is always present
        ...schema,
      }));

      // Ensure at least one state var for LangGraph to be triggered
      if (stateArray.length === 0) {
        stateArray = [{ name: 'input', type: 'string' }];
      }

      const flowDef = {
        name: flowName,
        description: flowDescription,
        nodes,
        edges,
        globalState: {
          states: stateArray, // Use the converted stateSchema with fallback
          tools: flowTools,
        },
        settings: {
          version: '1.0.0',
          created: currentFlowId ? undefined : new Date().toISOString(),
          updated: new Date().toISOString(),
        },
        mcpContext: exportContext(),
        id: currentFlowId,
        _id: currentFlowId,
      };
      let data;

      // Debug mode: execute with breakpoints or pause on every node, show DebugPanel when paused
      if (debugMode && executorBackend === 'python') {
        const result = await executeFlowDebug(flowDef, { input: executePrompt }, {
          breakpoints: breakpoints.length > 0 ? breakpoints : undefined,
          pauseOnEveryNode: breakpoints.length === 0,
        });
        setExecuteModalOpen(false);
        setDebugExecutionId(result.execution_id);
        setSnackbar({ open: true, message: 'Debug execution started. Use the Debug Panel to step through.', severity: 'info' });
        setExecuteLoading(false);
        return;
      }

      // Try streaming execution first if enabled
      if (streamingMode && executorBackend === 'python') {
        console.log('🚀 Using streaming execution');
        const input = { input: executePrompt };
        const streamConnection = await startStreamingExecution(flowDef, input);

        if (streamConnection) {
          // Streaming execution started successfully
          setExecuteResponse('Streaming execution started...');
          setExecuteModalOpen(false); // Close modal to show real-time execution
          return; // Exit early, streaming will handle the rest
        }
        // Fallback to traditional execution
        console.log('⚠️ Streaming failed, falling back to traditional execution');
      }

      // Traditional execution (fallback or when streaming disabled)
      if (executorBackend === 'python') {
        // Call Python backend (correct endpoint)
        const pythonBackendUrl = getLangGraphBaseUrl();
        const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
        const res = await fetch(`${pythonBackendUrl}/execute-flow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            flow: flowDef,
            input: { input: executePrompt },
            user_id: flowDef?.userId || 'frontend-user',
            tenant_id: tenantId || undefined,
            timeout_settings: {
              langgraph_continuation_timeout: flowSettings.langgraphContinuationTimeout,
              background_task_timeout: flowSettings.backgroundTaskTimeout,
            },
          }),
        });
        data = await res.json();
        const executionId = data?.execution_id;
        const initialStatus = data?.status;

        // If server already returned full result (e.g. status paused/completed), use it immediately
        const resultForDisplay = data?.result ?? data;
        const displayText = toDisplayString(
          resultForDisplay?.ai_response ?? resultForDisplay?.message ?? data?.ai_response ?? resultForDisplay,
          'Flow completed successfully'
        );

        if (initialStatus === 'paused' || initialStatus === 'completed' || initialStatus === 'complete' || initialStatus === 'success') {
          setExecuteResponse(displayText);
          if (executionId) {
            setDebugExecutionId(executionId);
            setExecuteModalOpen(false);
            setSnackbar({
              open: true,
              message: initialStatus === 'paused' ? 'Flow paused. Use the Debug Panel to inspect or continue.' : 'Flow finished. Use the Debug Panel to inspect execution.',
              severity: 'info',
            });
          }
        } else if (executionId) {
          setExecuteResponse('Flow started. Polling for result...');
          setDebugExecutionId(executionId);
          setExecuteModalOpen(false);
          let pollCount = 0;
          const poll = async () => {
            while (pollCount < 45) {
              await new Promise((r) => setTimeout(r, 2000));
              pollCount++;
              try {
                const statusData = await getFlowStatus(executionId);
                const s = statusData?.status;
                const res = statusData?.result ?? statusData;
                const text = toDisplayString(
                  res?.ai_response ?? res?.message ?? statusData?.ai_response ?? res,
                  'Flow completed successfully'
                );
                if (s === 'paused' || s === 'completed' || s === 'complete' || s === 'success') {
                  setExecuteResponse(text);
                  setDebugExecutionId(executionId);
                  setExecuteModalOpen(false);
                  setSnackbar({
                    open: true,
                    message: s === 'paused' ? 'Flow paused. Use the Debug Panel to inspect or continue.' : 'Flow finished. Use the Debug Panel to inspect execution.',
                    severity: 'info',
                  });
                  return;
                }
              } catch (pollErr) {
                console.warn('Poll error:', pollErr);
              }
            }
            setExecuteResponse(toDisplayString(data, 'Flow may still be running. Check execution tracker.'));
          };
          poll();
        } else {
          setExecuteResponse(displayText);
        }
      } else {
        // Call Node.js backend (executeAgenticFlow uses Python backend under the hood)
        const { response, err } = await executeAgenticFlow(id, { input: { input: executePrompt } });
        if (response) {
          // For Node.js backend, extract the nested response content (ensure string to avoid [object Object])
          const responseContent = response.result?.data ?? response.data ?? response.result;
          setExecuteResponse(toDisplayString(responseContent, 'No response received'));
          // Show Debug Panel when flow paused or completed (so user can inspect execution)
          if (response.executionId) {
            setDebugExecutionId(response.executionId);
            setExecuteModalOpen(false);
            setSnackbar({
              open: true,
              message: response.status === 'paused' ? 'Flow paused. Use the Debug Panel to inspect or continue.' : 'Flow finished. Use the Debug Panel to inspect execution.',
              severity: 'info',
            });
          }
        } else {
          setExecuteResponse(err?.message || 'Error executing flow');
        }
      }
      setSnackbar({ open: true, message: 'Flow executed successfully!', severity: 'success' });
    } catch (error) {
      setExecuteResponse('Error executing flow');
      setSnackbar({ open: true, message: 'Error executing flow', severity: 'error' });
    }
    setExecuteLoading(false);
  };

  // Load all saved flows
  const loadSavedFlows = useCallback(async () => {
    try {
      setFlowsLoading(true);
      const { response, err } = await getAgenticFlows();
      if (response) {
        setSavedFlows(response.flows || []);
      } else {
        console.error('Error loading flows:', err);
        setSavedFlows([]);
      }
    } catch (error) {
      console.error('Error loading flows:', error);
      setSavedFlows([]);
    } finally {
      setFlowsLoading(false);
    }
  }, []);

  // Load a specific flow by id
  const deleteFlow = async (flowId, flowName) => {
    if (!flowId || flowId === 'undefined' || flowId === 'null') {
      setSnackbar({ open: true, message: 'Invalid flow ID provided', severity: 'error' });
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete the flow "${flowName}"? This action cannot be undone.`);

    if (!confirmDelete) {
      return;
    }

    try {
      const { response, err } = await deleteAgenticFlow(flowId);

      if (response) {
        setSnackbar({ open: true, message: 'Flow deleted successfully!', severity: 'success' });
        // Refresh the flows list
        await loadSavedFlows();
        // Clear current flow if it was the deleted one
        if (currentFlowId === flowId) {
          setCurrentFlowId(null);
          setFlowName('');
          setFlowDescription('');
          setNodes([]);
          setEdges([]);
          setFlowStates([]);
          setFlowTools([]);
          setBreakpoints([]);
          localStorage.removeItem('agenticFlowBuilder_lastFlowId');
        }
      } else {
        setSnackbar({ open: true, message: err?.message || 'Failed to delete flow', severity: 'error' });
      }
    } catch (error) {
      console.error('Error deleting flow:', error);
      setSnackbar({ open: true, message: 'Error deleting flow', severity: 'error' });
    }
  };

  const loadFlow = async (flowId) => {
    if (!flowId || flowId === 'undefined' || flowId === 'null') {
      setSnackbar({ open: true, message: 'Invalid flow ID provided', severity: 'error' });
      return;
    }

    setLoadingFlowId(flowId);
    try {
      const { response, err } = await getAgenticFlowById(flowId);

      if (response && response.flow) {
        const { flow } = response;

        setNodes(flow.nodes || []); // Always set loaded nodes
        setEdges(flow.edges || []);
        setBreakpoints(flow.breakpoints || []);

        // Convert the loaded state array back to stateSchema object format
        const loadedStates = flow.globalState?.states || [];
        const loadedStateSchema = {};
        const loadedGlobalState = {};

        // Get all state variable names
        const stateVarNames = [];
        loadedStates.forEach((state) => {
          if (state.name) {
            stateVarNames.push(state.name);
            loadedStateSchema[state.name] = {
              type: state.type || 'string',
              defaultValue: state.defaultValue || '',
              description: state.description || '',
              persistent: state.persistent || false,
              nodes: state.nodes || [], // Preserve nodes that use this state var
            };
            loadedGlobalState[state.name] = state.defaultValue || '';
          }
        });

        setStateSchema(loadedStateSchema);
        setGlobalState((prev) => ({ ...prev, ...loadedGlobalState }));

        // Process and update loaded nodes to ensure they have correct stateVars
        const processedNodes = (flow.nodes || []).map((node) => {
          // Make sure node.data is initialized
          const nodeData = node.data || {};

          // First, preserve any existing stateVars from the loaded node
          const existingStateVars = Array.isArray(nodeData.stateVars) ? [...nodeData.stateVars] : [];

          // Now look for state variables in the stateSchema that are associated with this node
          const nodeId = node.id;
          const associatedStateVars = [];

          // Check each state variable to see if it's associated with this node
          Object.entries(loadedStateSchema).forEach(([varName, varInfo]) => {
            // If this node is mentioned in any state variable's nodes list, associate it
            if (Array.isArray(varInfo.nodes) && varInfo.nodes.includes(nodeId)) {
              associatedStateVars.push(varName);
            }

            // Legacy support: If the state var name exists in node.stateVars already
            if (existingStateVars.includes(varName)) {
              if (!associatedStateVars.includes(varName)) {
                associatedStateVars.push(varName);
              }

              // Also update the stateSchema to include this node
              loadedStateSchema[varName].nodes = [
                ...(loadedStateSchema[varName].nodes || []),
                nodeId,
              ];
            }
          });

          // Combine both sets of state vars (existing and associated), with no duplicates
          const combinedStateVars = [...new Set([...existingStateVars, ...associatedStateVars])];

          const updatedNode = {
            ...node,
            data: {
              ...nodeData,
              stateVars: combinedStateVars,
            },
          };

          return updatedNode;
        });

        // Set processed nodes with initialized stateVars
        setNodes(processedNodes);
        setFlowStates(flow.globalState?.states || []);
        setFlowTools(flow.globalState?.tools || []);
        setFlowName(flow.name || 'Loaded Flow');
        setFlowDescription(flow.description || '');
        setCurrentFlowId(flow.id || flow._id);
        setFlowVersions(flow.versions || []);

        // Set the original flow name for Save As functionality
        setOriginalFlowName(flow.name || 'Loaded Flow');

        // Mark that the flow has been manually saved (since it was loaded from a saved flow)
        setHasBeenManuallySaved(true);

        // Load flow settings
        if (flow.settings) {
          const loadedSettings = {
            // Execution Settings
            enableParallelExecution: flow.settings.enableParallelExecution ?? true,
            autoSaveState: flow.settings.autoSaveState ?? true,
            enableExecutionLogging: flow.settings.enableExecutionLogging ?? false,
            enableVariableSubstitution: flow.settings.enableVariableSubstitution ?? true,
            enableDebugMode: flow.settings.enableDebugMode ?? false,
            maxExecutionTime: flow.settings.maxExecutionTime ?? 300,
            executionTimeout: flow.settings.executionTimeout ?? 30000,

            // Error Handling
            onError: flow.settings.onError ?? 'stop',
            maxRetryAttempts: flow.settings.maxRetryAttempts ?? 3,
            retryDelay: flow.settings.retryDelay ?? 5,
            logAllErrors: flow.settings.logAllErrors ?? true,
            errorWorkflowId: flow.settings.errorWorkflowId ?? null,

            // State Management
            persistStateBetweenRuns: flow.settings.persistStateBetweenRuns ?? true,
            enableStateValidation: flow.settings.enableStateValidation ?? false,
            autoSaveStateVariables: flow.settings.autoSaveStateVariables ?? true,
            enableStateEncryption: flow.settings.enableStateEncryption ?? false,
            stateCleanupInterval: flow.settings.stateCleanupInterval ?? 24,
            maxStateSize: flow.settings.maxStateSize ?? 10,

            // Security & Access
            enableSandboxedExecution: flow.settings.enableSandboxedExecution ?? true,
            requireAuthentication: flow.settings.requireAuthentication ?? false,
            apiRateLimit: flow.settings.apiRateLimit ?? 60,
            enableAuditLogging: flow.settings.enableAuditLogging ?? false,
            validateInputData: flow.settings.validateInputData ?? true,
            sessionTimeout: flow.settings.sessionTimeout ?? 30,

            // Performance & Optimization
            enableCaching: flow.settings.enableCaching ?? true,
            enableCompression: flow.settings.enableCompression ?? false,
            enablePerformanceMonitoring: flow.settings.enablePerformanceMonitoring ?? false,
            optimizeMemoryUsage: flow.settings.optimizeMemoryUsage ?? true,
            cacheTTL: flow.settings.cacheTTL ?? 3600,
            maxConcurrentNodes: flow.settings.maxConcurrentNodes ?? 5,

            // Integration & External Services
            enableApiCalls: flow.settings.enableApiCalls ?? true,
            enableWebhookNotifications: flow.settings.enableWebhookNotifications ?? false,
            enableExternalDatabaseConnections: flow.settings.enableExternalDatabaseConnections ?? false,
            enableEmailNotifications: flow.settings.enableEmailNotifications ?? true,
            defaultApiTimeout: flow.settings.defaultApiTimeout ?? 30,
            webhookRetryAttempts: flow.settings.webhookRetryAttempts ?? 3,
          };
          setFlowSettings(loadedSettings);
        }

        // IMPORTANT: Import MCP context AFTER nodes are processed
        try {
          if (flow.mcpContext && importContext && typeof flow.mcpContext === 'object') {
            importContext(flow.mcpContext);
          }

          // Restore all node vars into MCP context after importContext
          // Use setTimeout to ensure this happens after the context is imported
          setTimeout(() => {
            try {
              if (setVar) {
                Object.entries(loadedStateSchema).forEach(([key, schema]) => {
                  if (Array.isArray(schema.nodes) && schema.nodes.length > 0) {
                    schema.nodes.forEach((nodeId) => {
                      setVar(key, schema.defaultValue, 'node', nodeId, {
                        type: schema.type,
                        description: schema.description,
                        persistent: schema.persistent,
                        updatedBy: 'system',
                      });
                    });
                  }
                });
              }
            } catch (mcpError) {
              console.error('Error setting MCP variables:', mcpError);
              // Don't fail the entire load for MCP errors
            }
          }, 100); // Small delay to ensure context is ready
        } catch (mcpError) {
          console.error('Error importing MCP context:', mcpError);
          // Don't fail the entire load for MCP errors
        }

        setLoadDialogOpen(false);

        // Save the loaded flow ID to localStorage for auto-loading
        localStorage.setItem('agenticFlowBuilder_lastFlowId', flow.id || flow._id);

        setSnackbar({ open: true, message: 'Flow loaded successfully!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: err?.message || 'Failed to load flow', severity: 'error' });
        setLoadDialogOpen(false); // Close modal even on error
      }
    } catch (error) {
      console.error('Error loading flow:', error);
      setSnackbar({ open: true, message: 'Error loading flow', severity: 'error' });
      setLoadDialogOpen(false); // Close modal even on error
    } finally {
      setLoadingFlowId(null);
      // Ensure modal closes even if there are any hanging operations
      setTimeout(() => {
        setLoadDialogOpen(false);
      }, 1000);
    }
  };

  // Auto-load last opened flow
  const loadLastOpenedFlow = useCallback(async () => {
    try {
      const lastFlowId = localStorage.getItem('agenticFlowBuilder_lastFlowId');
      if (lastFlowId && lastFlowId !== 'undefined' && lastFlowId !== 'null') {
        // Validate that the flow exists before trying to load it
        try {
          const { response } = await getAgenticFlowById(lastFlowId);
          if (response && response.flow) {
            await loadFlow(lastFlowId);
          } else {
            // Flow doesn't exist, clear the invalid ID
            localStorage.removeItem('agenticFlowBuilder_lastFlowId');
          }
        } catch (validationError) {
          // Flow doesn't exist or API error, clear the invalid ID
          localStorage.removeItem('agenticFlowBuilder_lastFlowId');
        }
      }
    } catch (error) {
      console.error('Error auto-loading last flow:', error);
      // Clear invalid flow ID from localStorage
      localStorage.removeItem('agenticFlowBuilder_lastFlowId');
    }
  }, []);

  // Load flows on mount and auto-load last flow
  useEffect(() => {
    loadSavedFlows();
    loadLastOpenedFlow();
  }, [loadSavedFlows, loadLastOpenedFlow]);

  // Integrate with global search
  useEffect(() => {
    const handleGlobalSearch = (event) => {
      const { query } = event.detail;
      if (query && query.toLowerCase().includes('flow')) {
        loadSavedFlows().then(() => {
          setLoadDialogOpen(true);
        });
      }
    };
    window.addEventListener('globalSearch', handleGlobalSearch);
    return () => window.removeEventListener('globalSearch', handleGlobalSearch);
  }, [loadSavedFlows]);

  // Listen for the 'openAgenticFlowLoader' event and open the load dialog when triggered.
  useEffect(() => {
    const handleOpenAgenticFlowLoader = () => setLoadDialogOpen(true);
    window.addEventListener('openAgenticFlowLoader', handleOpenAgenticFlowLoader);
    return () => window.removeEventListener('openAgenticFlowLoader', handleOpenAgenticFlowLoader);
  }, []);

  // Node click handler: only sets selectedNode and opens dialog
  const handleNodeClick = (event, node) => {
    setSelectedNode(node);
    setSelectedScope('node');
    setSelectedTab(2); // Switch to State Management tab
  };

  // Function to synchronize state variables when nodes are updated
  const syncStateVariables = useCallback(() => {
    if (!nodes || nodes.length === 0) return;

    const updatedStateSchema = { ...stateSchema };
    let hasChanges = false;

    // First, collect all state variables that are actually used by nodes
    const usedStateVars = new Set();
    nodes.forEach((node) => {
      if (node.data && Array.isArray(node.data.stateVars)) {
        node.data.stateVars.forEach((stateVar) => {
          usedStateVars.add(stateVar);
        });
      }
    });

    // Remove state variables from schema that are no longer used by any node
    Object.keys(updatedStateSchema).forEach((stateVar) => {
      if (!usedStateVars.has(stateVar)) {
        delete updatedStateSchema[stateVar];
        hasChanges = true;
      }
    });

    // Add or update state variables that are used by nodes
    nodes.forEach((node) => {
      if (node.data && Array.isArray(node.data.stateVars)) {
        node.data.stateVars.forEach((stateVar) => {
          if (!updatedStateSchema[stateVar]) {
            // Create new state variable entry
            updatedStateSchema[stateVar] = {
              type: 'string',
              defaultValue: '',
              description: `State variable for ${node.data.label || node.id}`,
              persistent: false,
              nodes: [node.id],
            };
            hasChanges = true;
          } else {
            // Update existing state variable to include this node
            const stateNodes = updatedStateSchema[stateVar].nodes || [];
            if (!stateNodes.includes(node.id)) {
              updatedStateSchema[stateVar] = {
                ...updatedStateSchema[stateVar],
                nodes: [...stateNodes, node.id],
              };
              hasChanges = true;
            }
          }
        });
      }
    });

    if (hasChanges) {
      setStateSchema(updatedStateSchema);
    }
  }, [nodes, stateSchema]);

  // Track recent deletions to prevent immediate recreation
  const recentDeletionsRef = useRef(new Set());

  // Sync state variables when nodes change
  useEffect(() => {
    // Skip sync if we just deleted variables
    if (recentDeletionsRef.current.size > 0) {
      // Clear recent deletions after a short delay
      setTimeout(() => {
        recentDeletionsRef.current.clear();
      }, 100);
      return;
    }

    syncStateVariables();
  }, [syncStateVariables]);

  // Handler to sync node stateVars array when a node var is deleted from MCP context
  const handleDeleteVar = (key, scope, nodeId) => {
    // Track this deletion to prevent immediate recreation
    recentDeletionsRef.current.add(key);

    // Remove from state schema
    setStateSchema((prev) => {
      const newSchema = { ...prev };
      delete newSchema[key];
      return newSchema;
    });

    // Remove from global state
    setGlobalState((prev) => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });

    // Remove from nodes - be more specific about which nodes to update
    setNodes((prevNodes) => prevNodes.map((node) => {
      if (node.data && Array.isArray(node.data.stateVars)) {
        const updatedStateVars = node.data.stateVars.filter((varName) => varName !== key);
        if (updatedStateVars.length !== node.data.stateVars.length) {
          return {
            ...node,
            data: {
              ...node.data,
              stateVars: updatedStateVars,
            },
          };
        }
      }
      return node;
    }));

    // Remove from MCP context
    if (deleteVar) {
      deleteVar(key, scope, nodeId);
    }
  };

  const [selectedScope, setSelectedScope] = useState('global');

  // Ensure setEdges is properly bound and working
  const handleSetEdges = useCallback((updater) => {
    if (typeof updater === 'function') {
      setEdges((prevEdges) => updater(prevEdges));
    } else {
      setEdges(updater);
    }
  }, [setEdges]);

  // Handler to delete an edge by id
  const handleEdgeDelete = useCallback((edge) => {
    setEdges((prev) => prev.filter((e) => e.id !== edge.id));
  }, [setEdges]);

  // Debug function to log state variable status
  const debugStateVariables = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('=== State Variable Debug ===');
      console.log('State Schema:', stateSchema);
      console.log('Global State:', globalState);
      console.log('Nodes with stateVars:', nodes.filter((n) => n.data?.stateVars?.length > 0).map((n) => ({
        id: n.id,
        label: n.data?.label,
        stateVars: n.data?.stateVars,
      })));
      if (exportContext) {
        console.log('MCP Context:', exportContext());
      }
    }
  }, [stateSchema, globalState, nodes, exportContext]);

  // Keyboard shortcuts handlers
  const keyboardHandlers = {
    save: () => {
      handleOpenSaveDialog();
      autoSaveFlow(true); // Force immediate save
    },
    load: () => setLoadDialogOpen(true),
    new: () => handleNewFlow(),
    execute: () => handleExecuteFlow(false),
    execute_debug: () => handleExecuteFlow(true),
    undo: () => {
      // TODO: Implement undo functionality
      console.log('Undo requested');
    },
    redo: () => {
      // TODO: Implement redo functionality
      console.log('Redo requested');
    },
    copy: () => {
      // TODO: Implement copy functionality
      console.log('Copy requested');
    },
    paste: () => {
      // TODO: Implement paste functionality
      console.log('Paste requested');
    },
    delete: () => {
      // TODO: Implement delete functionality
      console.log('Delete requested');
    },
    select_all: () => {
      // TODO: Implement select all functionality
      console.log('Select all requested');
    },
    escape: () => {
      // Clear any open dialogs or selections
      setLoadDialogOpen(false);
      setSaveDialogOpen(false);
      setImportDialogOpen(false);
    },
    search: () => {
      // Focus search in palette
      const searchInput = document.querySelector('input[placeholder="Search nodes..."]');
      if (searchInput) {
        searchInput.focus();
        searchInput.select();
      }
    },
    help: () => {
      navigate('/agentic-workflow-help');
    },
    shortcuts: () => {
      setShortcutsHelpOpen(true);
    },
    settings: () => {
      // TODO: Open settings
      console.log('Settings requested');
    },
    export: () => {
      handleExportFlow();
    },
    import: () => {
      setImportDialogOpen(true);
    },
    run: () => {
      handleExecuteFlow();
    },
    duplicate: () => {
      // TODO: Implement duplicate functionality
      console.log('Duplicate requested');
    },
    group: () => {
      // TODO: Implement group functionality
      console.log('Group requested');
    },
    ungroup: () => {
      // TODO: Implement ungroup functionality
      console.log('Ungroup requested');
    },
  };

  // Initialize keyboard shortcuts
  useKeyboardShortcuts(keyboardHandlers);

  // Add debug logging when state variables change
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugStateVariables();
    }
  }, [debugStateVariables]);

  // Export flow to JSON
  const handleExportFlow = () => {
    try {
      // Prepare the flow data for export
      const updatedStateSchema = { ...stateSchema };
      nodes.forEach((node) => {
        if (node.data && Array.isArray(node.data.stateVars)) {
          node.data.stateVars.forEach((stateVar) => {
            if (updatedStateSchema[stateVar]) {
              const stateNodes = updatedStateSchema[stateVar].nodes || [];
              if (!stateNodes.includes(node.id)) {
                updatedStateSchema[stateVar] = {
                  ...updatedStateSchema[stateVar],
                  nodes: [...stateNodes, node.id],
                };
              }
            } else {
              updatedStateSchema[stateVar] = {
                type: 'string',
                defaultValue: '',
                description: `State variable for ${node.data.label || node.id}`,
                persistent: false,
                nodes: [node.id],
              };
            }
          });
        }
      });

      const stateArray = Object.entries(updatedStateSchema).map(([key, schema]) => ({
        name: key,
        ...schema,
      }));

      const flowData = {
        name: flowName,
        description: flowDescription,
        nodes,
        edges,
        globalState: {
          states: stateArray,
          tools: flowTools,
        },
        settings: {
          version: '1.0.0',
          created: new Date().toISOString(),
          updated: new Date().toISOString(),
          ...flowSettings,
        },
        mcpContext: exportContext ? exportContext() : null,
        exportVersion: '1.0.0',
        exportedAt: new Date().toISOString(),
      };

      const jsonString = JSON.stringify(flowData, null, 2);
      setExportJson(jsonString);
      setExportDialogOpen(true);
    } catch (error) {
      console.error('Error exporting flow:', error);
      setSnackbar({ open: true, message: 'Error exporting flow', severity: 'error' });
    }
  };

  // Import flow from JSON
  const handleImportFlow = (jsonData) => {
    try {
      const flowData = JSON.parse(jsonData);

      // Validate the imported data
      if (!flowData.name || !flowData.nodes || !Array.isArray(flowData.nodes)) {
        throw new Error('Invalid flow data format');
      }

      // Clear current state
      setNodes([]);
      setEdges([]);
      setStateSchema({});
      setGlobalState({});
      setFlowStates([]);
      setFlowTools([]);

      // Load the imported data
      setNodes(flowData.nodes || []);
      setEdges(flowData.edges || []);
      setFlowName(flowData.name || 'Imported Flow');
      setFlowDescription(flowData.description || '');
      setCurrentFlowId(null); // Reset flow ID for imported flow

      // Load state schema and global state
      if (flowData.globalState?.states) {
        const importedStateSchema = {};
        const importedGlobalState = {};

        flowData.globalState.states.forEach((state) => {
          if (state.name) {
            importedStateSchema[state.name] = {
              type: state.type || 'string',
              defaultValue: state.defaultValue || '',
              description: state.description || '',
              persistent: state.persistent || false,
              nodes: state.nodes || [],
            };
            importedGlobalState[state.name] = state.defaultValue || '';
          }
        });

        setStateSchema(importedStateSchema);
        setGlobalState(importedGlobalState);
      }

      // Load flow tools
      if (flowData.globalState?.tools) {
        setFlowTools(flowData.globalState.tools);
      }

      // Load flow settings
      if (flowData.settings) {
        const importedSettings = {
          // Execution Settings
          enableParallelExecution: flowData.settings.enableParallelExecution ?? true,
          autoSaveState: flowData.settings.autoSaveState ?? true,
          enableExecutionLogging: flowData.settings.enableExecutionLogging ?? false,
          enableVariableSubstitution: flowData.settings.enableVariableSubstitution ?? true,
          enableDebugMode: flowData.settings.enableDebugMode ?? false,
          maxExecutionTime: flowData.settings.maxExecutionTime ?? 300,
          executionTimeout: flowData.settings.executionTimeout ?? 30000,

          // Error Handling
          onError: flowData.settings.onError ?? 'stop',
          maxRetryAttempts: flowData.settings.maxRetryAttempts ?? 3,
          retryDelay: flowData.settings.retryDelay ?? 5,
          logAllErrors: flowData.settings.logAllErrors ?? true,

          // State Management
          persistStateBetweenRuns: flowData.settings.persistStateBetweenRuns ?? true,
          enableStateValidation: flowData.settings.enableStateValidation ?? false,
          autoSaveStateVariables: flowData.settings.autoSaveStateVariables ?? true,
          enableStateEncryption: flowData.settings.enableStateEncryption ?? false,
          stateCleanupInterval: flowData.settings.stateCleanupInterval ?? 24,
          maxStateSize: flowData.settings.maxStateSize ?? 10,

          // Security & Access
          enableSandboxedExecution: flowData.settings.enableSandboxedExecution ?? true,
          requireAuthentication: flowData.settings.requireAuthentication ?? false,
          apiRateLimit: flowData.settings.apiRateLimit ?? 60,
          enableAuditLogging: flowData.settings.enableAuditLogging ?? false,
          validateInputData: flowData.settings.validateInputData ?? true,
          sessionTimeout: flowData.settings.sessionTimeout ?? 30,

          // Performance & Optimization
          enableCaching: flowData.settings.enableCaching ?? true,
          enableCompression: flowData.settings.enableCompression ?? false,
          enablePerformanceMonitoring: flowData.settings.enablePerformanceMonitoring ?? false,
          optimizeMemoryUsage: flowData.settings.optimizeMemoryUsage ?? true,
          cacheTTL: flowData.settings.cacheTTL ?? 3600,
          maxConcurrentNodes: flowData.settings.maxConcurrentNodes ?? 5,

          // Integration & External Services
          enableApiCalls: flowData.settings.enableApiCalls ?? true,
          enableWebhookNotifications: flowData.settings.enableWebhookNotifications ?? false,
          enableExternalDatabaseConnections: flowData.settings.enableExternalDatabaseConnections ?? false,
          enableEmailNotifications: flowData.settings.enableEmailNotifications ?? true,
          defaultApiTimeout: flowData.settings.defaultApiTimeout ?? 30,
          webhookRetryAttempts: flowData.settings.webhookRetryAttempts ?? 3,
        };
        setFlowSettings(importedSettings);
      }

      // Import MCP context if available
      if (flowData.mcpContext && importContext) {
        try {
          importContext(flowData.mcpContext);
        } catch (mcpError) {
          console.error('Error importing MCP context:', mcpError);
          // Don't fail the entire import for MCP errors
        }
      }

      // For imported flows, clear the last flow ID since this is a new flow
      localStorage.removeItem('agenticFlowBuilder_lastFlowId');

      setImportDialogOpen(false);
      setSnackbar({ open: true, message: 'Flow imported successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error importing flow:', error);
      setSnackbar({ open: true, message: `Error importing flow: ${error.message}`, severity: 'error' });
    }
  };

  // Download JSON file
  const handleDownloadJson = () => {
    try {
      const blob = new Blob([exportJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${flowName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_flow.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
      setSnackbar({ open: true, message: 'Flow exported successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error downloading JSON:', error);
      setSnackbar({ open: true, message: 'Error downloading JSON file', severity: 'error' });
    }
  };

  // Handle file upload for import
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = e.target.result;
          handleImportFlow(jsonData);
        } catch (error) {
          console.error('Error reading file:', error);
          setSnackbar({ open: true, message: 'Error reading JSON file', severity: 'error' });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleNewFlow = () => {
    // Clear all state
    setNodes([]);
    setEdges([]);
    setStateSchema({});
    setGlobalState({});
    setFlowStates([]);
    setBreakpoints([]);
    setFlowTools([]);
    setFlowName('New Flow');
    setFlowDescription('');
    setCurrentFlowId(null);
    setSelectedNode(null);
    setSelectedScope('global');
    setSelectedTab(0);

    // Reset Save As state
    setOriginalFlowName('New Flow');

    // Reset manual save flag for new flow
    setHasBeenManuallySaved(false);

    // Clear MCP context
    if (importContext) {
      importContext({ vars: { global: {}, flow: {}, node: {} } });
    }

    // Clear the last flow ID from localStorage
    localStorage.removeItem('agenticFlowBuilder_lastFlowId');

    setSnackbar({ open: true, message: 'New flow created', severity: 'info' });
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return (
          <FlowDesignTab
            nodes={nodes}
            setNodes={setNodes}
            edges={edges}
            setEdges={handleSetEdges}
            globalState={globalState}
            setGlobalState={setGlobalState}
            darkMode={darkMode}
            onNodeClick={handleNodeClick}
            stateSchema={stateSchema}
            setStateSchema={setStateSchema}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            onNewFlow={handleNewFlow}
            onLoadFlow={() => {
              loadSavedFlows();
              setLoadDialogOpen(true);
            }}
            onSaveFlow={handleOpenSaveDialog}
            onExecuteFlow={() => handleExecuteFlow(false)}
            breakpoints={breakpoints}
            onToggleBreakpoint={(nodeId) => {
              setBreakpoints((prev) =>
                prev.includes(nodeId) ? prev.filter((b) => b !== nodeId) : [...prev, nodeId]
              );
            }}
            onNotify={(msg, severity) => setSnackbar({ open: true, message: msg, severity: severity || 'info' })}
            onNodeDelete={(nodeId) => {
              setNodes((prev) => prev.filter((n) => n.id !== nodeId));
              setEdges((prev) => prev.filter((e) => e.source !== nodeId && e.target !== nodeId));
            }}
          />
        );
      case 1:
        return (
          <EdgesTab
            edges={edges}
            setEdges={setEdges}
            nodes={nodes}
            onAddEdge={() => setEdgeDialogOpen(true)}
            onEditEdge={(edge) => {
              setCurrentEdge(edge);
              setEdgeDialogOpen(true);
            }}
            onDeleteEdge={(edgeId) => setEdges((prev) => prev.filter((e) => e.id !== edgeId))}
          />
        );
      case 2:
        return (
          <StateManagementTab
            onCreateVar={(scope, nodeId) => {
              setCurrentStateVar({ scope, nodeId });
              setStateDialogOpen(true);
            }}
            onEditVar={(key, varData, scope, nodeId) => {
              setCurrentStateVar({
                key, ...varData, scope, nodeId,
              });
              setStateDialogOpen(true);
            }}
            onDeleteVar={handleDeleteVar}
            selectedNode={selectedNode}
            selectedScope={selectedScope}
            setSelectedScope={setSelectedScope}
            nodes={nodes}
          />
        );
      case 3:
        return (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <FlowSettingsTab
              flowSettings={flowSettings}
              setFlowSettings={setFlowSettings}
              versions={flowVersions}
              currentFlowId={currentFlowId}
              onRestoreVersion={(v) => {
                if (v?.nodes) setNodes(v.nodes);
                if (v?.edges) setEdges(v.edges);
                setSnackbar({ open: true, message: 'Version restored', severity: 'success' });
              }}
            />
          </Box>
        );
      case 4:
        return (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <MCPFlowIntegration
              flowId={currentFlowId}
              isTabMode
              onContextChange={(data) => {
                console.log('MCP Context Update:', data);
                // Handle context changes from MCP servers
                if (data.scope && data.key && data.value !== undefined) {
                  setVar(data.key, data.value, data.scope);
                }
              }}
              onToolExecuted={(data) => {
                console.log('MCP Tool Executed:', data);
                // Handle tool execution results
                if (data.result && data.toolName) {
                  setVar(`tool_result_${data.toolName}_${Date.now()}`, data.result, 'flow');
                }
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{
      height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative',
    }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Header row: Tabs + Actions */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{
          px: 2, pt: 2, pb: 1, borderBottom: 1, borderColor: 'divider', width: '100%', pr: { xs: 0, md: 8 },
        }}
      >
        {/* Flow name + editable */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Click to rename flow">
            <Typography
              variant="h6"
              noWrap
              onClick={() => handleOpenSaveDialog()}
              sx={{
                flexShrink: 0, minWidth: 0, maxWidth: { xs: '100%', md: 240 }, overflow: 'hidden', textOverflow: 'ellipsis',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
              }}
            >
              {flowName}
            </Typography>
          </Tooltip>
          {autoSaveEnabled && hasBeenManuallySaved && (
            <Tooltip
              title={
                autoSaveStatus === 'saving'
                  ? 'Saving...'
                  : autoSaveStatus === 'saved'
                    ? 'All changes saved'
                    : autoSaveStatus === 'error'
                      ? 'Save failed – check connection'
                      : 'Auto-save'
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {autoSaveStatus === 'saving' && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      border: '1px solid #1976d2',
                      borderTop: '1px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                )}
                {autoSaveStatus === 'saved' && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      backgroundColor: '#4caf50',
                      borderRadius: '50%',
                    }}
                  />
                )}
                {autoSaveStatus === 'error' && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      backgroundColor: '#f44336',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          )}
        </Box>
        {/* Tabs */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FlowBuilderTabs selectedTab={selectedTab} onTabChange={setSelectedTab} />
        </Box>
        {/* Essential Actions - Compact Layout */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Auto-save indicator */}
          <Tooltip title={hasBeenManuallySaved ? (autoSaveEnabled ? 'Auto-save on' : 'Auto-save off') : 'Save flow first to enable'}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch
                checked={autoSaveEnabled && hasBeenManuallySaved}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                size="small"
                disabled={!hasBeenManuallySaved}
              />
              {autoSaveStatus === 'saving' && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    border: '1px solid #1976d2',
                    borderTop: '1px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              )}
              {autoSaveStatus === 'saved' && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: '#4caf50',
                    borderRadius: '50%',
                  }}
                />
              )}
              {autoSaveStatus === 'error' && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: '#f44336',
                    borderRadius: '50%',
                  }}
                />
              )}
            </Box>
          </Tooltip>

          {/* Core Actions */}
          <Tooltip title="New Flow">
            <IconButton onClick={handleNewFlow} size="small">
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save Flow (Ctrl+S)">
            <IconButton onClick={handleOpenSaveDialog} size="small">
              <SaveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Load Flow">
            <IconButton
              onClick={() => {
                loadSavedFlows();
                setLoadDialogOpen(true);
              }}
              size="small"
            >
              <LoadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Execute Flow">
            <IconButton
              onClick={() => handleExecuteFlow(false)}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
              size="small"
            >
              <PlayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Debug Flow (pause on every node)">
            <IconButton
              onClick={() => handleExecuteFlow(true)}
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'secondary.dark' },
              }}
              size="small"
            >
              <BugReportIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Keyboard Shortcuts (Ctrl+?)">
            <IconButton onClick={() => setShortcutsHelpOpen(true)} size="small">
              <KeyboardIcon />
            </IconButton>
          </Tooltip>

          {/* More Options Menu */}
          <Tooltip title="More Options">
            <IconButton
              onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {/* Tab content below header */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: selectedTab === 3 ? 'auto' : 'hidden', // Allow scrolling for settings tab
      }}
      >
        {renderTabContent()}
      </Box>

      {/* MCP Integration - Removed sidebar, now only in tab */}

      <EdgeDialog
        open={edgeDialogOpen}
        onClose={() => {
          setEdgeDialogOpen(false);
          setCurrentEdge(null);
        }}
        edge={currentEdge}
        nodes={nodes}
        onSave={(edge) => {
          if (!edge.source || !edge.target) return;

          setEdges((prev) => {
            const existing = prev.findIndex((e) => e.id === edge.id);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = edge;
              return updated;
            }
            return [...prev, { ...edge, id: edge.id || `edge_${Date.now()}` }];
          });
          setEdgeDialogOpen(false);
          setCurrentEdge(null);
        }}
      />

      <StateVariableDialog
        open={stateDialogOpen}
        onClose={() => {
          setStateDialogOpen(false);
          setCurrentStateVar(null);
        }}
        stateVar={currentStateVar}
        nodeId={currentStateVar?.scope === 'node' ? (currentStateVar?.nodeId || selectedNode?.id || currentNodeId) : undefined}
        scope={currentStateVar?.scope}
        onSave={(stateVar) => {
          if (!stateVar.key) return;

          // Always use the scope and nodeId passed as props
          const scope = currentStateVar?.scope;
          const nodeId = scope === 'node' ? (currentStateVar?.nodeId || selectedNode?.id || currentNodeId) : undefined;

          // Get node ID if it's an edit
          const existingNodeIds = currentStateVar && stateSchema[currentStateVar.key]?.nodes || [];

          setStateSchema((prev) => ({
            ...prev,
            [stateVar.key]: {
              type: stateVar.type,
              defaultValue: stateVar.defaultValue,
              description: stateVar.description,
              persistent: stateVar.persistent,
              nodes: existingNodeIds, // Keep track of nodes using this state var
            },
          }));

          if (!(stateVar.key in globalState)) {
            setGlobalState((prev) => ({
              ...prev,
              [stateVar.key]: stateVar.defaultValue,
            }));
          }

          // Use MCP context for all scopes
          if (setVar) {
            if (scope === 'node' && nodeId) {
              setVar(stateVar.key, stateVar.defaultValue, 'node', nodeId, {
                type: stateVar.type || 'string',
                description: stateVar.description || '',
                persistent: stateVar.persistent,
                updatedBy: 'user',
              });
            } else if (scope === 'flow') {
              setVar(stateVar.key, stateVar.defaultValue, 'flow', null, {
                type: stateVar.type || 'string',
                description: stateVar.description || '',
                persistent: stateVar.persistent,
                updatedBy: 'user',
              });
            } else {
              setVar(stateVar.key, stateVar.defaultValue, 'global', null, {
                type: stateVar.type || 'string',
                description: stateVar.description || '',
                persistent: stateVar.persistent,
                updatedBy: 'user',
              });
            }
          }

          // Explicitly add this state var to a selected node if one is selected
          if ((selectedNode || nodeId) && scope === 'node') {
            setNodes((prevNodes) => prevNodes.map((node) => {
              // Defensive: ensure node.data and node.data.stateVars exist
              const stateVars = Array.isArray(node.data?.stateVars) ? node.data.stateVars : [];
              if (node.id === (selectedNode?.id || nodeId)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    stateVars: Array.from(new Set([...stateVars, stateVar.key])), // Ensure uniqueness
                  },
                };
              }
              return node;
            }));
          }
        }}
      />

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Flow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Flow Name"
            type="text"
            fullWidth
            variant="outlined"
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={flowDescription}
            onChange={(e) => setFlowDescription(e.target.value)}
          />

          {/* Save As indicator */}
          {currentFlowId && flowName !== originalFlowName && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light',
            }}
            >
              <Typography variant="body2" sx={{ color: 'info.dark', fontWeight: 500 }}>
                💾 Save As Operation
              </Typography>
              <Typography variant="caption" display="block" sx={{ color: 'info.dark' }}>
                This will create a new flow named "
                {flowName}
                " while keeping the original "
                {originalFlowName}
                " unchanged.
              </Typography>
            </Box>
          )}

          <FormControlLabel
            control={<Checkbox checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} />}
            label="Also save as Workflow Template"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveFlow} color="primary" disabled={executeLoading}>
            {executeLoading ? 'Saving...' : (
              <>
                <SaveIcon />
                {' '}
                {currentFlowId && flowName !== originalFlowName ? 'Save As' : 'Save Flow'}
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Load Flow
              {savedFlows.length > 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (
                  {savedFlows.length}
                  {' '}
                  available)
                </Typography>
              )}
            </Typography>
            <IconButton onClick={() => setLoadDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {flowsLoading || loadingFlowId ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {flowsLoading ? 'Loading flows...' : 'Loading flow...'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {flowsLoading ? 'Please wait while we fetch the saved flows.' : 'Please wait while we load the flow.'}
              </Typography>
            </Box>
          ) : (
            savedFlows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No saved flows found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create and save your first flow to get started
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button variant="contained" onClick={() => setLoadDialogOpen(false)}>
                    Create New Flow
                  </Button>
                  <Button variant="outlined" onClick={() => loadSavedFlows()}>
                    Refresh
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select a flow to load:
                </Typography>
                <List sx={{ p: 0 }}>
                  {savedFlows
                    .filter((flow) => flow.id || flow._id) // Only show flows with valid IDs
                    .map((flow) => (
                      <ListItem
                        key={flow.id || flow._id}
                        onClick={() => loadFlow(flow.id || flow._id)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                            transform: 'translateY(-1px)',
                            boxShadow: 1,
                          },
                        }}
                      >
                        <ListItemIcon>
                          <TimelineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={(
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {flow.name}
                            </Typography>
                        )}
                          secondary={(
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {flow.description || 'No description'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={`${flow.nodes ? flow.nodes.length : 0} nodes`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${flow.edges ? flow.edges.length : 0} edges`}
                                  size="small"
                                  variant="outlined"
                                />
                                {flow.updatedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  Updated:
                                  {' '}
                                  {new Date(flow.updatedAt).toLocaleDateString()}
                                </Typography>
                                )}
                              </Box>
                            </Box>
                        )}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                loadFlow(flow.id || flow._id);
                              }}
                              color="primary"
                              title="Load this flow"
                              disabled={loadingFlowId === (flow.id || flow._id)}
                              size="small"
                            >
                              {loadingFlowId === (flow.id || flow._id) ? (
                                <Box sx={{
                                  width: 20, height: 20, border: '2px solid', borderColor: 'primary.main', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite',
                                }}
                                />
                              ) : (
                                <LoadIcon />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteFlow(flow.id || flow._id, flow.name);
                              }}
                              color="error"
                              title="Delete this flow"
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>
              </Stack>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setLoadDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          {savedFlows.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => loadSavedFlows()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={executeModalOpen} onClose={() => setExecuteModalOpen(false)}>
        <DialogTitle>Execute Flow</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={(
              <Switch
                checked={executorBackend === 'python'}
                onChange={(e) => setExecutorBackend(e.target.checked ? 'python' : 'node')}
                color="primary"
              />
            )}
            label={executorBackend === 'python' ? 'Python Backend' : 'Node.js Backend'}
            sx={{ mb: 2 }}
          />
          {executorBackend === 'python' && (
            <FormControlLabel
              control={(
                <Switch
                  checked={streamingMode}
                  onChange={(e) => setStreamingMode(e.target.checked)}
                  color="primary"
                />
              )}
              label="Enable Real-time Streaming"
              sx={{ mb: 2 }}
            />
          )}
          {executorBackend === 'python' && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={debugMode}
                    onChange={(e) => setDebugMode(e.target.checked)}
                    color="secondary"
                  />
                )}
                label="Debug Mode (pause on every node)"
              />
              {debugMode && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                  Execution will pause at each node. Use the Debug Panel (top-right) to Step, Run to End, or inspect state.
                </Typography>
              )}
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Input Prompt"
            type="text"
            fullWidth
            variant="outlined"
            value={executePrompt}
            onChange={(e) => setExecutePrompt(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Response"
            type="text"
            fullWidth
            variant="outlined"
            value={typeof executeResponse === 'string' ? executeResponse : (executeResponse != null ? JSON.stringify(executeResponse, null, 2) : '')}
            InputProps={{
              readOnly: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExecuteModalOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleRunFlowWithPrompt}
            color={debugMode ? 'secondary' : 'primary'}
            variant="contained"
            disabled={executeLoading}
          >
            {executeLoading ? 'Executing...' : debugMode ? 'Debug Flow' : 'Run Flow'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug Panel - shown when flow runs (paused or completed) - use Dialog so it's always visible */}
      {debugExecutionId && (
        <Dialog
          open
          onClose={() => setDebugExecutionId(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          <DebugPanel
            executionId={debugExecutionId}
            flowId={currentFlowId}
            embedded
            onClose={() => setDebugExecutionId(null)}
            onComplete={() => {
              setSnackbar({ open: true, message: 'Debug execution completed.', severity: 'success' });
              setDebugExecutionId(null);
            }}
          />
        </Dialog>
      )}

      {/* Streaming Execution UI */}
      {streamingConnection && (
        <Dialog open onClose={() => {}} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Real-time Flow Execution</Typography>
              <Chip
                icon={<StreamingIcon />}
                label="Streaming Mode"
                color="primary"
                size="small"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {/* Execution Progress */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Progress:
                {' '}
                {executionProgress}
                %
              </Typography>
              <LinearProgress
                variant="determinate"
                value={executionProgress}
                sx={{ mb: 1 }}
              />
              {currentExecutionNode && (
                <Typography variant="body2" color="text.secondary">
                  Current Node:
                  {' '}
                  {currentExecutionNode}
                </Typography>
              )}
            </Box>

            {/* Node Statuses */}
            {Object.keys(nodeStatuses).length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Node Status
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(nodeStatuses).map(([nodeId, status]) => (
                    <Chip
                      key={nodeId}
                      label={`${nodeId}: ${status}`}
                      size="small"
                      color={status === 'completed' ? 'success' : status === 'error' ? 'error' : 'default'}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Real-time Updates */}
            {realTimeUpdates.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Live Updates
                </Typography>
                <Box sx={{
                  maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1,
                }}
                >
                  {realTimeUpdates.slice(-10).map((update, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      <Chip label={update.type} size="small" sx={{ mr: 1 }} />
                      {update.node_id && `${update.node_id}: `}
                      {typeof update.message === 'string' ? update.message : (update.message != null ? JSON.stringify(update.message) : 'Update received')}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Execution Log */}
            {executionLog.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Execution Log
                </Typography>
                <Box sx={{
                  maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1,
                }}
                >
                  {executionLog.slice(-10).map((log, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5, fontFamily: 'monospace' }}>
                      [
                      {new Date(log.timestamp).toLocaleTimeString()}
                      ]
                      {' '}
                      {typeof log.message === 'string' ? log.message : (log.message != null ? JSON.stringify(log.message) : '')}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Response */}
            {executeResponse && executeResponse !== 'Streaming execution started...' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Final Response
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={typeof executeResponse === 'string' ? executeResponse : JSON.stringify(executeResponse ?? {}, null, 2)}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {canRecover && (
              <Button
                onClick={() => handleErrorRecovery(errorRecoveryState.selectedExecution)}
                disabled={isRecovering}
                color="warning"
                startIcon={<RecoveryIcon />}
              >
                {isRecovering ? 'Recovering...' : 'Recover Flow'}
              </Button>
            )}
            <Button
              onClick={() => {
                if (streamingConnection) {
                  streamingConnection.close();
                  setStreamingConnection(null);
                }
                setExecuteResponse('');
                setExecutionProgress(0);
                setCurrentExecutionNode(null);
                setExecutionLog([]);
                setNodeStatuses({});
                setRealTimeUpdates([]);
                setCanRecover(false);
              }}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Error Recovery Modal */}
      <ErrorRecoveryModal
        open={errorRecoveryState.isVisible}
        onClose={() => setErrorRecoveryState((prev) => ({ ...prev, isVisible: false }))}
        error={errorRecoveryState.error}
        recoveryOptions={errorRecoveryState.recoveryOptions}
        onRecover={handleErrorRecovery}
        isRecovering={isRecovering}
      />

      {/* Monitoring Dashboard Dialog */}
      <Dialog open={monitoringDialogOpen} onClose={() => setMonitoringDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Monitoring Dashboard</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={monitoringMode}
                    onChange={(e) => {
                      setMonitoringMode(e.target.checked);
                      if (e.target.checked) {
                        fetchDebugData();
                      }
                    }}
                    color="primary"
                  />
                )}
                label="Enable Monitoring"
              />
              <Button
                onClick={fetchDebugData}
                disabled={!monitoringMode}
                size="small"
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!monitoringMode ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Enable monitoring to view system performance, debug data, and execution metrics.
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* System Status */}
              {debugData?.status && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    System Status
                  </Typography>
                  <Paper sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Status:
                          {' '}
                          <Chip label={debugData.status.status || 'Unknown'} color="success" size="small" />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Uptime:
                          {' '}
                          {debugData.status.uptime || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Active Executions:
                          {' '}
                          {debugData.status.active_executions || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Executions:
                          {' '}
                          {debugData.status.total_executions || 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}

              {/* Performance Metrics */}
              {debugData?.performance && debugData.performance.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    {debugData.performance.map((metric, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              {metric.name || `Metric ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Value:
                              {' '}
                              {metric.value || 'N/A'}
                            </Typography>
                            {metric.description && (
                              <Typography variant="caption" color="text.secondary">
                                {metric.description}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Error Log */}
              {debugData?.errors && debugData.errors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Recent Errors
                  </Typography>
                  <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
                    {debugData.errors.slice(-10).map((error, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>
                            {error.timestamp}
                            :
                          </strong>
                          {' '}
                          {error.message}
                        </Typography>
                        {error.details && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                            Details:
                            {' '}
                            {error.details}
                          </Typography>
                        )}
                      </Alert>
                    ))}
                  </Paper>
                </Box>
              )}

              {debugData && (
                <Alert severity="success">
                  Monitoring data refreshed successfully at
                  {' '}
                  {new Date().toLocaleTimeString()}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonitoringDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export JSON Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Flow to JSON</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This JSON contains your complete flow definition including nodes, edges, state variables, and settings.
          </Typography>
          <TextField
            multiline
            rows={15}
            fullWidth
            variant="outlined"
            value={exportJson}
            InputProps={{
              readOnly: true,
            }}
            sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDownloadJson} color="primary" variant="contained">
            Download JSON File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import JSON Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Flow from JSON</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a JSON file to import a flow. This will replace your current flow.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImportIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Choose JSON File
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Supported format: Flow JSON files exported from this application
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp
        open={shortcutsHelpOpen}
        onClose={() => setShortcutsHelpOpen(false)}
      />

      {/* More Options Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          handleNewFlow();
          setMoreMenuAnchor(null);
        }}
        >
          <AddIcon sx={{ mr: 1 }} />
          New Flow
        </MenuItem>
        <MenuItem onClick={() => {
          loadSavedFlows();
          setLoadDialogOpen(true);
          setMoreMenuAnchor(null);
        }}
        >
          <LoadIcon sx={{ mr: 1 }} />
          Load Flow
        </MenuItem>
        <MenuItem onClick={() => {
          handleOpenSaveDialog();
          autoSaveFlow(true);
          setMoreMenuAnchor(null);
        }}
        >
          <SaveIcon sx={{ mr: 1 }} />
          Save Flow
        </MenuItem>
        <MenuItem
          onClick={() => {
            const currentName = flowName;
            setFlowName(`${currentName} - Copy`);
            handleOpenSaveDialog();
            setFlowName(currentName);
            setMoreMenuAnchor(null);
          }}
          disabled={!currentFlowId}
        >
          <SaveIcon sx={{ mr: 1 }} />
          Save As (Create Copy)
        </MenuItem>
        <MenuItem onClick={() => {
          handleExportFlow();
          setMoreMenuAnchor(null);
        }}
        >
          <ExportIcon sx={{ mr: 1 }} />
          Export to JSON
        </MenuItem>
        <MenuItem onClick={() => {
          setImportDialogOpen(true);
          setMoreMenuAnchor(null);
        }}
        >
          <ImportIcon sx={{ mr: 1 }} />
          Import from JSON
        </MenuItem>
        <MenuItem onClick={() => {
          setShortcutsHelpOpen(true);
          setMoreMenuAnchor(null);
        }}
        >
          <KeyboardIcon sx={{ mr: 1 }} />
          Keyboard Shortcuts
        </MenuItem>
        <MenuItem onClick={() => {
          navigate('/agentic-workflow-help');
          setMoreMenuAnchor(null);
        }}
        >
          <HelpIcon sx={{ mr: 1 }} />
          Flow Builder Help
        </MenuItem>
        <MenuItem onClick={() => {
          setOpenDebugPanelDialog(true);
          setMoreMenuAnchor(null);
        }}
        >
          <BugReportIcon sx={{ mr: 1 }} />
          Open Debug Panel...
        </MenuItem>
        <MenuItem onClick={() => {
          handleOpenMonitoring();
          setMoreMenuAnchor(null);
        }}
        >
          <TimelineIcon sx={{ mr: 1 }} />
          Monitoring Dashboard
        </MenuItem>
      </Menu>

      {/* Open Debug Panel dialog - paste execution ID manually */}
      <Dialog open={openDebugPanelDialog} onClose={() => setOpenDebugPanelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Open Debug Panel</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste an execution ID (e.g. exec_xxx) to inspect a paused flow.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Execution ID"
            value={debugPanelExecutionIdInput}
            onChange={(e) => setDebugPanelExecutionIdInput(e.target.value)}
            placeholder="exec_1eb7ffa79bb140d1bf905c3d3c122198"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDebugPanelDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const id = debugPanelExecutionIdInput?.trim();
              if (id) {
                setDebugExecutionId(id);
                setOpenDebugPanelDialog(false);
                setDebugPanelExecutionIdInput('');
                setSnackbar({ open: true, message: 'Debug Panel opened.', severity: 'info' });
              }
            }}
            disabled={!debugPanelExecutionIdInput?.trim()}
          >
            Open
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AgenticFlowBuilderWithContext(props) {
  return (
    <FlowContextProvider>
      <AgenticFlowBuilder {...props} />
    </FlowContextProvider>
  );
}

export default AgenticFlowBuilderWithContext;
